<template>
  <!-- Step 7, the last one. Mirrors the question iOS asks on its legacy email
       signup (EmailViewModel) and stores the answer in the same place,
       `users.attribution_tag`, using the same nine options so the two
       platforms' answers group together.

       Optional on purpose: it's a question for us, not for the user, so it must
       never stand between someone and the app. Finish works untouched. -->
  <div>
    <OnBoardingStepIndicator :current="7" />

    <div class="back-link mb-2" @click="backTapped">
      <span class="material-icons">arrow_left</span>
      <span>{{ $t('on_boarding.profile_steps.back') }}</span>
    </div>

    <h2 class="auth-heading">{{ $t('on_boarding.profile_source_page.title') }}</h2>
    <p class="auth-sub">{{ $t('on_boarding.profile_source_page.subtitle') }}</p>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>

    <ul class="sources" role="listbox" :aria-label="$t('on_boarding.profile_source_page.title')">
      <li v-for="source in sources" :key="source.value">
        <button
          type="button"
          class="source"
          :class="{ 'source--selected': source.value === selected }"
          role="option"
          :aria-selected="source.value === selected"
          @click="choose(source.value)"
        >
          {{ source.label }}
        </button>
      </li>
    </ul>

    <button
      type="button"
      class="btn pro-btn pro-btn--accent w-100 mt-3"
      :disabled="isLoading"
      @click="submit"
    >
      <template v-if="isLoading">
        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
      </template>
      <template v-else>
        <span class="material-icons">check_circle</span>
      </template>
      <span>{{ selected ? $t('on_boarding.profile_source_page.finish') : $t('on_boarding.profile_source_page.skip') }}</span>
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import OnBoardingStepIndicator from './OnBoardingStepIndicator.vue';
import { ATTRIBUTION_SOURCES, type AttributionSource } from '../../../utils/helpers/signupProfile';

export default defineComponent({
  name: 'OnBoardingProfileSourcePage',
  components: { OnBoardingStepIndicator },
  data() {
    return {
      selected: this.$store.state.onBoarding.profileAttributionTag as string | null,
    };
  },
  computed: {
    // Every label is named by a literal $t() call rather than a key built from
    // `source.labelKey`. The client bundle is assembled by scanning these call
    // sites (lib/i18n_client_bundle.rb), and a key stitched together at runtime
    // is invisible to that scan — the strings would simply be absent from the
    // bundle and the step would render raw key names.
    sources(): Array<AttributionSource & { label: string }> {
      const labels: Record<string, string> = {
        search_engine: this.$t('on_boarding.profile_source_page.search_engine'),
        chatgpt: this.$t('on_boarding.profile_source_page.chatgpt'),
        grok: this.$t('on_boarding.profile_source_page.grok'),
        muse: this.$t('on_boarding.profile_source_page.muse'),
        facebook_ad: this.$t('on_boarding.profile_source_page.facebook_ad'),
        instagram_ad: this.$t('on_boarding.profile_source_page.instagram_ad'),
        social_media: this.$t('on_boarding.profile_source_page.social_media'),
        word_of_mouth: this.$t('on_boarding.profile_source_page.word_of_mouth'),
        podcast: this.$t('on_boarding.profile_source_page.podcast'),
        youtube: this.$t('on_boarding.profile_source_page.youtube'),
        influencer: this.$t('on_boarding.profile_source_page.influencer'),
        other: this.$t('on_boarding.profile_source_page.other'),
      };
      return ATTRIBUTION_SOURCES.map((source) => ({ ...source, label: labels[source.labelKey] }));
    },
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
  },
  methods: {
    // Tapping the chosen option again clears it — the only way back to "no
    // answer" once something has been picked.
    choose(value: string) {
      this.selected = this.selected === value ? null : value;
    },
    submit() {
      this.$store.dispatch('onBoarding/submitProfileSource', this.selected);
    },
    backTapped() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/popPage');
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
  },
});
</script>

<style scoped>
.auth-heading {
  font-family: 'Montserrat', Helvetica, Arial, sans-serif;
  font-size: clamp(1.4rem, 2.4vw, 1.75rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: #fff;
  margin: 0 0 0.35rem;
}
.auth-sub {
  color: var(--pro-text-muted);
  font-size: 0.9rem;
  margin: 0 0 0.9rem;
}
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--pro-text-muted);
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
}
.back-link:hover { color: #fff; }

/* Pills that wrap, so nine options stay compact enough to fit the panel
   without scrolling. */
.sources {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.source {
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  border: 1px solid var(--pro-border-strong);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.source:hover {
  background: rgba(255, 255, 255, 0.09);
  color: #fff;
}
.source--selected {
  border-color: var(--yellow, #fddf00);
  background: rgba(253, 223, 0, 0.14);
  color: #fff;
}
</style>
